// =============================================================================
// Origin UI — src/pages/requests/RequestFormPage.tsx
// Create new request or edit existing (Draft/Returned only).
// Handles both /requests/new and /requests/:id/edit routes.
// Phase 3 Task 3.1: Multi-step wizard with progress bar.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { requestApi, referenceApi } from '@/services/requestApi';
import { api } from '@/services/apiService';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import type { CreateRequestInput, SelectionInput, RequestType, DepartmentRef, OfficeLocationRef, ManagerRef, RequestEmployeeRef, RequestDetail } from '@/types/api';
import styles from './requests.module.css';

// ─── Default selections pre-ticked for new requests ───
const DEFAULT_SELECTIONS: SelectionInput[] = [
  { selectionType: 'Hardware', selectionValue: 'Desktop' },
  { selectionType: 'Hardware', selectionValue: 'Camera' },
  { selectionType: 'Hardware', selectionValue: 'Headset' },
  { selectionType: 'Hardware', selectionValue: 'Laptop' },
  { selectionType: 'Hardware', selectionValue: 'iPhone' },
  { selectionType: 'Citrix', selectionValue: 'Citrix Access' },
  { selectionType: 'Software', selectionValue: 'Vista' },
  { selectionType: 'Software', selectionValue: 'Diva' },
];

// ─── Offboarding: tick ALL hardware, ALL software, ALL folders ───
const OFFBOARDING_SELECTIONS: SelectionInput[] = [
  { selectionType: 'Hardware', selectionValue: 'Desktop' },
  { selectionType: 'Hardware', selectionValue: 'Camera' },
  { selectionType: 'Hardware', selectionValue: 'Headset' },
  { selectionType: 'Hardware', selectionValue: 'Laptop' },
  { selectionType: 'Hardware', selectionValue: 'iPhone' },
  { selectionType: 'Hardware', selectionValue: 'iPad' },
  { selectionType: 'Citrix', selectionValue: 'Citrix Access' },
  { selectionType: 'Software', selectionValue: 'LSEG' },
  { selectionType: 'Software', selectionValue: 'Vista' },
  { selectionType: 'Software', selectionValue: 'Diva' },
  { selectionType: 'Software', selectionValue: 'X2' },
  { selectionType: 'Software', selectionValue: 'LiveMTM' },
  { selectionType: 'Software', selectionValue: 'Vega' },
  { selectionType: 'Software', selectionValue: 'ICE Web' },
  { selectionType: 'Software', selectionValue: 'ICE IM' },
  { selectionType: 'FolderAccess', selectionValue: 'Trading Shared' },
  { selectionType: 'FolderAccess', selectionValue: 'Finance Shared' },
  { selectionType: 'FolderAccess', selectionValue: 'HR Shared' },
  { selectionType: 'FolderAccess', selectionValue: 'Operations Shared' },
  { selectionType: 'FolderAccess', selectionValue: 'Technology Shared' },
  { selectionType: 'FolderAccess', selectionValue: 'Risk Management Shared' },
  { selectionType: 'FolderAccess', selectionValue: 'Admin Shared' },
  { selectionType: 'FolderAccess', selectionValue: 'Analytics Shared' },
];

// ─── Transfer: no defaults (requester picks what changes) ───
const TRANSFER_SELECTIONS: SelectionInput[] = [];

// ─── Software items ───
const SOFTWARE_ITEMS = ['LSEG', 'Vista', 'Diva', 'X2', 'LiveMTM', 'Vega', 'ICE Web', 'ICE IM'];

// ─── Folder access items ───
const FOLDER_ACCESS_ITEMS = ['Trading Shared', 'Finance Shared', 'HR Shared', 'Operations Shared', 'Technology Shared', 'Risk Management Shared', 'Admin Shared', 'Analytics Shared'];

// ─── Mobile package options ───
const MOBILE_PACKAGES = ['Standard', 'Traveller', 'Frequent Traveller'];

// ─── Employment type options (Onboarding only) — matches the employee record vocabulary ───
const EMPLOYMENT_TYPES = ['Permanent', 'Contract'];

// ─── Wizard step definitions ───
const WIZARD_STEPS = [
  { key: 'type', label: 'Request Type & Employee' },
  { key: 'hardware', label: 'Hardware' },
  { key: 'software', label: 'Software' },
  { key: 'access', label: 'Access & Folders' },
  { key: 'review', label: 'Review & Submit' },
];

export function RequestFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);

  // Form state
  const [requestType, setRequestType] = useState<RequestType>('Onboarding');
  const [oqtEmployeeId, setOqtEmployeeId] = useState('');
  const [fullName, setFullName] = useState('');
  const [preferredInitials, setPreferredInitials] = useState('');
  const [oqtEmailAddress, setOqtEmailAddress] = useState('');
  const [designation, setDesignation] = useState('');
  const [department, setDepartment] = useState('');
  const [officeLocation, setOfficeLocation] = useState('');
  const [lineManagerEmail, setLineManagerEmail] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [employmentType, setEmploymentType] = useState('Permanent');
  // Tracks whether the user has manually edited the email, so auto-fill from OQT ID stops once they do.
  const [emailEdited, setEmailEdited] = useState(false);
  const [selections, setSelections] = useState<SelectionInput[]>([]);

  // Remarks and extra fields
  const [softwareRemarks, setSoftwareRemarks] = useState('');
  const [hardwareRemarks, setHardwareRemarks] = useState('');
  const [iPhoneType, setIPhoneType] = useState('');
  const [deskPhoneExt, setDeskPhoneExt] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobilePackage, setMobilePackage] = useState('Standard');
  const [folderRemarks, setFolderRemarks] = useState<Record<string, string>>({});
  const [appBatchDistribution, setAppBatchDistribution] = useState('');
  const [emailDistributionLists, setEmailDistributionLists] = useState('');
  const [otherAccess, setOtherAccess] = useState('');

  // Offboarding instruction fields
  const [forwardEmailTo, setForwardEmailTo] = useState('');
  const [outOfOfficeMessage, setOutOfOfficeMessage] = useState('');
  const [lastDayReminder, setLastDayReminder] = useState('');
  const [handoverInstructions, setHandoverInstructions] = useState('');
  const [deactivationDate, setDeactivationDate] = useState('');

  // Transfer: optional new line manager
  const [newLineManagerEmail, setNewLineManagerEmail] = useState('');

  // Collapse state for transfer sections
  const [hwExpanded, setHwExpanded] = useState(true);
  const [swExpanded, setSwExpanded] = useState(true);

  // Transfer: per-folder action (NoChange / Add / Revoke)
  const [folderTransferActions, setFolderTransferActions] = useState<Record<string, 'NoChange' | 'Add' | 'Revoke'>>(
    Object.fromEntries(FOLDER_ACCESS_ITEMS.map(f => [f, 'NoChange']))
  );

  // Reference data
  const [departments, setDepartments] = useState<DepartmentRef[]>([]);
  const [locations, setLocations] = useState<OfficeLocationRef[]>([]);
  const [managers, setManagers] = useState<ManagerRef[]>([]);

  // Existing-employee picker (Offboarding / Transfer subject)
  const [employees, setEmployees] = useState<RequestEmployeeRef[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existingRequest, setExistingRequest] = useState<RequestDetail | null>(null);
  const [stepErrors, setStepErrors] = useState<string[]>([]);

  useEffect(() => {
    loadReferenceData();
    if (isEdit && id) {
      loadExistingRequest(id);
    } else {
      // Pre-tick default selections for new requests
      setSelections(DEFAULT_SELECTIONS.map(d => ({ ...d })));
    }
  }, [id]);

  async function loadReferenceData() {
    try {
      const [depts, locs] = await Promise.all([
        referenceApi.departments(),
        referenceApi.officeLocations(),
      ]);
      setDepartments(depts);
      setLocations(locs);
    } catch (err: any) {
      setError('Failed to load reference data');
    }

    // Load managers separately so a failure doesn't block the rest of the form
    try {
      const mgrs = await referenceApi.managers();
      setManagers([...mgrs].sort((a, b) => a.fullName.localeCompare(b.fullName)));
    } catch {
      console.warn('Could not load managers list');
    }

    // Load the active-employee list for the Offboarding/Transfer subject picker.
    try {
      const emps = await referenceApi.employees();
      setEmployees(emps);
    } catch {
      console.warn('Could not load employees list');
    }
  }

  // ─── Department template: fetch and MERGE with defaults when department changes (new requests only) ───
  async function applyDepartmentTemplate(deptName: string) {
    if (!deptName || isEdit) return; // Only apply on new requests
    try {
      const templates = await api.get<{ selectionType: string; selectionValue: string; additionalMetadata?: string }[]>(
        `/api/admin/department-templates?department=${encodeURIComponent(deptName)}`
      );
      if (templates && templates.length > 0) {
        // Separate free-text template items from selection-based ones
        const freeTextTypes = ['AppBatchDistribution', 'EmailDistributionLists', 'OtherAccess'];

        // Start with the standard defaults
        const merged: SelectionInput[] = DEFAULT_SELECTIONS.map(d => ({ ...d }));

        // Add selection-based template items that aren't already in defaults
        templates
          .filter(t => !freeTextTypes.includes(t.selectionType))
          .forEach(t => {
            const exists = merged.some(m => m.selectionType === t.selectionType && m.selectionValue === t.selectionValue);
            if (!exists) {
              merged.push({
                selectionType: t.selectionType,
                selectionValue: t.selectionValue,
                additionalMetadata: t.additionalMetadata || undefined,
              });
            }
          });

        setSelections(merged);

        // Apply folder remarks from templates
        const newFolderRemarks: Record<string, string> = {};
        templates
          .filter(t => t.selectionType === 'FolderAccess' && t.additionalMetadata)
          .forEach(t => { newFolderRemarks[t.selectionValue] = t.additionalMetadata!; });
        if (Object.keys(newFolderRemarks).length > 0) {
          setFolderRemarks(prev => ({ ...prev, ...newFolderRemarks }));
        }

        // Apply free-text defaults
        const findText = (type: string) => templates.find(t => t.selectionType === type);
        const appBatch = findText('AppBatchDistribution');
        if (appBatch) setAppBatchDistribution(appBatch.selectionValue);
        const emailDist = findText('EmailDistributionLists');
        if (emailDist) setEmailDistributionLists(emailDist.selectionValue);
        const other = findText('OtherAccess');
        if (other) setOtherAccess(other.selectionValue);
      }
      // If no templates configured for this department, keep standard defaults unchanged
    } catch {
      // Silently fail — templates are optional
      console.warn('Could not load department templates for', deptName);
    }
  }

  function handleDepartmentChange(deptName: string) {
    setDepartment(deptName);
    applyDepartmentTemplate(deptName);
  }

  async function loadExistingRequest(requestId: string) {
    try {
      setLoading(true);
      const req = await requestApi.getById(requestId);
      setExistingRequest(req);
      setRequestType(req.requestType);
      setSelectedEmployeeId(req.employeeId || '');
      setOqtEmployeeId(req.oqtEmployeeId);
      setFullName(req.fullName);
      setPreferredInitials(req.preferredInitials || '');
      setOqtEmailAddress(req.oqtEmailAddress);
      setEmailEdited(true);
      setDesignation(req.designation);
      setDepartment(req.department);
      setOfficeLocation(req.officeLocation);
      setLineManagerEmail(req.lineManagerEmail);
      setEffectiveDate(req.effectiveDate && !req.effectiveDate.startsWith('0001') ? req.effectiveDate.split('T')[0] : '');

      // Load selections (excluding special remark entries)
      const specialTypes = ['SoftwareRemarks', 'HardwareRemarks', 'iPhoneType', 'DeskPhoneExt', 'MobileNumber', 'MobilePackage', 'FolderRemarks', 'AppBatchDistribution', 'EmailDistributionLists', 'OtherAccess', 'ForwardEmailTo', 'OutOfOfficeMessage', 'LastDayReminder', 'HandoverInstructions', 'DeactivationDate', 'NewLineManager', 'EmploymentType'];
      setSelections(req.selections
        .filter(s => !specialTypes.includes(s.selectionType))
        .map(s => ({
          selectionType: s.selectionType,
          selectionValue: s.selectionValue,
          additionalMetadata: s.additionalMetadata || undefined,
        })));

      // Load special fields from selections
      const find = (type: string) => req.selections.find(s => s.selectionType === type);
      const remarksEntry = find('SoftwareRemarks');
      if (remarksEntry) setSoftwareRemarks(remarksEntry.selectionValue);
      const hwRemarks = find('HardwareRemarks');
      if (hwRemarks) setHardwareRemarks(hwRemarks.selectionValue);
      const iPhone = find('iPhoneType');
      if (iPhone) setIPhoneType(iPhone.selectionValue);
      const deskExt = find('DeskPhoneExt');
      if (deskExt) setDeskPhoneExt(deskExt.selectionValue);
      const mobNum = find('MobileNumber');
      if (mobNum) setMobileNumber(mobNum.selectionValue);
      const mobPkg = find('MobilePackage');
      if (mobPkg) setMobilePackage(mobPkg.selectionValue);
      const empType = find('EmploymentType');
      if (empType) setEmploymentType(empType.selectionValue);
      const appBatch = find('AppBatchDistribution');
      if (appBatch) setAppBatchDistribution(appBatch.selectionValue);
      const emailDist = find('EmailDistributionLists');
      if (emailDist) setEmailDistributionLists(emailDist.selectionValue);
      const other = find('OtherAccess');
      if (other) setOtherAccess(other.selectionValue);

      // Offboarding instruction fields
      const fwdEmail = find('ForwardEmailTo');
      if (fwdEmail) setForwardEmailTo(fwdEmail.selectionValue);
      const ooo = find('OutOfOfficeMessage');
      if (ooo) setOutOfOfficeMessage(ooo.selectionValue);
      const lastDay = find('LastDayReminder');
      if (lastDay) setLastDayReminder(lastDay.selectionValue);
      const handover = find('HandoverInstructions');
      if (handover) setHandoverInstructions(handover.selectionValue);
      const deactDate = find('DeactivationDate');
      if (deactDate) setDeactivationDate(deactDate.selectionValue);

      // Transfer: new line manager
      const newMgr = find('NewLineManager');
      if (newMgr) setNewLineManagerEmail(newMgr.selectionValue);

      // Load folder-level remarks from additionalMetadata
      const folderSels = req.selections.filter(s => s.selectionType === 'FolderAccess' && s.additionalMetadata);
      const remarks: Record<string, string> = {};
      folderSels.forEach(s => { remarks[s.selectionValue] = s.additionalMetadata || ''; });
      setFolderRemarks(remarks);

    } catch (err: any) {
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  }

  function toggleSelection(type: string, value: string) {
    setSelections(prev => {
      const exists = prev.find(s => s.selectionType === type && s.selectionValue === value);
      if (exists) return prev.filter(s => !(s.selectionType === type && s.selectionValue === value));
      return [...prev, { selectionType: type, selectionValue: value }];
    });
  }

  function isSelected(type: string, value: string) {
    return selections.some(s => s.selectionType === type && s.selectionValue === value);
  }

  function updateFolderRemark(folder: string, remark: string) {
    setFolderRemarks(prev => ({ ...prev, [folder]: remark }));
  }

  function setFolderAction(folder: string, action: 'NoChange' | 'Add' | 'Revoke') {
    setFolderTransferActions(prev => ({ ...prev, [folder]: action }));
    // Sync with selections: add or remove FolderAccess selection based on action
    if (action === 'NoChange') {
      setSelections(prev => prev.filter(s => !(s.selectionType === 'FolderAccess' && s.selectionValue === folder)));
    } else {
      setSelections(prev => {
        const without = prev.filter(s => !(s.selectionType === 'FolderAccess' && s.selectionValue === folder));
        return [...without, { selectionType: 'FolderAccess', selectionValue: folder, additionalMetadata: action }];
      });
    }
  }

  function buildInput(): CreateRequestInput {
    const allSelections: SelectionInput[] = selections.map(s => {
      if (s.selectionType === 'FolderAccess' && folderRemarks[s.selectionValue]) {
        return { ...s, additionalMetadata: folderRemarks[s.selectionValue] };
      }
      return s;
    });

    if (softwareRemarks.trim()) allSelections.push({ selectionType: 'SoftwareRemarks', selectionValue: softwareRemarks.trim() });
    if (hardwareRemarks.trim()) allSelections.push({ selectionType: 'HardwareRemarks', selectionValue: hardwareRemarks.trim() });
    if (iPhoneType.trim() && isSelected('Hardware', 'iPhone')) allSelections.push({ selectionType: 'iPhoneType', selectionValue: iPhoneType.trim() });
    if (deskPhoneExt.trim()) allSelections.push({ selectionType: 'DeskPhoneExt', selectionValue: deskPhoneExt.trim() });
    if (mobileNumber.trim()) allSelections.push({ selectionType: 'MobileNumber', selectionValue: mobileNumber.trim() });
    if (mobilePackage) allSelections.push({ selectionType: 'MobilePackage', selectionValue: mobilePackage });
    if (appBatchDistribution.trim()) allSelections.push({ selectionType: 'AppBatchDistribution', selectionValue: appBatchDistribution.trim() });
    if (emailDistributionLists.trim()) allSelections.push({ selectionType: 'EmailDistributionLists', selectionValue: emailDistributionLists.trim() });
    if (otherAccess.trim()) allSelections.push({ selectionType: 'OtherAccess', selectionValue: otherAccess.trim() });
    if (requestType === 'Onboarding' && employmentType) allSelections.push({ selectionType: 'EmploymentType', selectionValue: employmentType });

    // Offboarding instruction fields (stored as selections)
    if (forwardEmailTo.trim()) allSelections.push({ selectionType: 'ForwardEmailTo', selectionValue: forwardEmailTo.trim() });
    if (outOfOfficeMessage.trim()) allSelections.push({ selectionType: 'OutOfOfficeMessage', selectionValue: outOfOfficeMessage.trim() });
    if (lastDayReminder.trim()) allSelections.push({ selectionType: 'LastDayReminder', selectionValue: lastDayReminder.trim() });
    if (handoverInstructions.trim()) allSelections.push({ selectionType: 'HandoverInstructions', selectionValue: handoverInstructions.trim() });
    if (deactivationDate) allSelections.push({ selectionType: 'DeactivationDate', selectionValue: deactivationDate });

    // Transfer: new line manager (stored as selection)
    if (newLineManagerEmail.trim()) allSelections.push({ selectionType: 'NewLineManager', selectionValue: newLineManagerEmail.trim() });

    const techItems = ['Fingerprint Access', 'Printer Access', 'AV', 'Office 365 License', 'Scanner Address Book', 'Added to Default DLs'];
    techItems.forEach(item => {
      allSelections.push({ selectionType: 'Technology', selectionValue: item });
    });

    return {
      requestType,
      employeeId: selectedEmployeeId || undefined,
      oqtEmployeeId: oqtEmployeeId.trim(),
      fullName: fullName.trim(),
      preferredInitials: preferredInitials.trim() || undefined,
      oqtEmailAddress: oqtEmailAddress.trim(),
      designation: designation.trim(),
      department,
      officeLocation,
      lineManagerEmail: lineManagerEmail.trim(),
      effectiveDate: effectiveDate || null,
      selections: allSelections,
    };
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError('');
      const input = buildInput();
      if (isEdit && id) {
        await requestApi.update(id, input);
      } else {
        await requestApi.create(input);
      }
      navigate('/requests');
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || err.message || 'Failed to save request');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAndSubmit() {
    // Validate all mandatory fields before submitting
    const errors = validateAllSteps();
    if (errors.length > 0) {
      setStepErrors(errors);
      setCurrentStep(0); // Jump back to Step 1 where the mandatory fields are
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    try {
      setSaving(true);
      setError('');
      setStepErrors([]);
      const input = buildInput();
      let requestId = id;
      if (isEdit && id) {
        await requestApi.update(id, input);
      } else {
        const created = await requestApi.create(input);
        requestId = created.requestId;
      }

      if (existingRequest?.status === 'Returned') {
        await requestApi.resubmit(requestId!);
      } else {
        await requestApi.submit(requestId!);
      }
      navigate('/requests');
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || err.message || 'Failed to submit request');
    } finally {
      setSaving(false);
    }
  }

  // ─── OQT ID change (Onboarding) — auto-derive the corporate email until edited ───
  function handleOqtIdChange(v: string) {
    setOqtEmployeeId(v);
    if (requestType === 'Onboarding' && !emailEdited) {
      const code = v.trim().toLowerCase();
      setOqtEmailAddress(code ? `${code}@omantrading.com` : '');
    }
  }

  // ─── Existing-employee picker (Offboarding / Transfer) ───
  function handleEmployeeSelect(empId: string) {
    setSelectedEmployeeId(empId);
    if (!empId) {
      // Cleared — blank the locked subject fields
      setOqtEmployeeId('');
      setFullName('');
      setOqtEmailAddress('');
      setDesignation('');
      setDepartment('');
      setOfficeLocation('');
      setLineManagerEmail('');
      return;
    }
    const emp = employees.find(e => e.employeeId === empId);
    if (!emp) return;
    setOqtEmployeeId(emp.vistaId || '');
    setFullName(emp.fullName);
    setOqtEmailAddress(emp.email);
    setDesignation(emp.designation);
    setDepartment(emp.department);
    setOfficeLocation(emp.officeLocation);
    setLineManagerEmail(emp.lineManagerEmail);
  }

  // ─── Handle request type change — reset selections to appropriate defaults ───
  function handleRequestTypeChange(newType: RequestType) {
    setRequestType(newType);
    if (!isEdit) {
      // Clear the subject identity so values from one type don't carry into another.
      // Offboarding/Transfer re-populate from the employee picker; Onboarding is free entry.
      setSelectedEmployeeId('');
      setEmploymentType('Permanent');
      setEmailEdited(false);
      setOqtEmployeeId('');
      setFullName('');
      setPreferredInitials('');
      setOqtEmailAddress('');
      setDesignation('');
      setDepartment('');
      setOfficeLocation('');
      setLineManagerEmail('');

      if (newType === 'Offboarding') {
        setSelections(OFFBOARDING_SELECTIONS.map(d => ({ ...d })));
        setHwExpanded(true);
        setSwExpanded(true);
      } else if (newType === 'Transfer') {
        setSelections(TRANSFER_SELECTIONS.map(d => ({ ...d })));
        setHwExpanded(false);
        setSwExpanded(false);
      } else {
        setSelections(DEFAULT_SELECTIONS.map(d => ({ ...d })));
        setHwExpanded(true);
        setSwExpanded(true);
      }
    }
  }

  // ─── Validation — only checked on Submit, not on navigation ───
  function validateAllSteps(): string[] {
    const errors: string[] = [];
    if ((requestType === 'Offboarding' || requestType === 'Transfer') && !selectedEmployeeId) {
      errors.push('An employee must be selected');
    }
    if (!fullName.trim()) errors.push('Full Name is required');
    if (!designation.trim()) errors.push('Designation is required');
    if (!department) errors.push('Department is required');
    if (!officeLocation) errors.push('Office Location is required');
    if (!lineManagerEmail) errors.push('Line Manager is required');
    if (!effectiveDate) errors.push('Effective Date is required');
    if (requestType === 'Offboarding' && deactivationDate && effectiveDate && deactivationDate > effectiveDate) {
      errors.push('System Access End Date cannot be after the Last Working Day');
    }
    return errors;
  }

  function goNext() {
    setStepErrors([]);
    setCurrentStep(prev => Math.min(prev + 1, WIZARD_STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setStepErrors([]);
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goToStep(step: number) {
    setStepErrors([]);
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ─── Cancel with confirmation ───
  function hasChanges(): boolean {
    // For new requests, check if anything has been filled in beyond defaults
    if (!isEdit) {
      return Boolean(
        oqtEmployeeId.trim() || fullName.trim() || oqtEmailAddress.trim() ||
        designation.trim() || department || officeLocation || lineManagerEmail ||
        effectiveDate || softwareRemarks.trim() || hardwareRemarks.trim() ||
        iPhoneType.trim() || deskPhoneExt.trim() || mobileNumber.trim() ||
        appBatchDistribution.trim() || emailDistributionLists.trim() || otherAccess.trim()
      );
    }
    // For edits, always assume changes may have been made
    return true;
  }

  function handleCancel() {
    if (hasChanges()) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave? Any changes will be lost.');
      if (!confirmed) return;
    }
    navigate('/requests');
  }

  // ─── Label helpers ───
  const isOffboarding = requestType === 'Offboarding';
  const isExistingEmployeeType = requestType === 'Offboarding' || requestType === 'Transfer';
  const effectiveDateLabel = requestType === 'Onboarding'
    ? 'Effective Date'
    : requestType === 'Transfer'
      ? 'Transfer Date'
      : 'Last Working Day';
  const hw = (onLabel: string, offLabel: string) => isOffboarding ? offLabel : onLabel;

  if (loading) return <LoadingSpinner message="Loading request..." />;

  const progressPercent = ((currentStep) / (WIZARD_STEPS.length - 1)) * 100;
  const countByType = (type: string) => selections.filter(s => s.selectionType === type).length;
  const getSelectedItems = (type: string): string[] => selections.filter(s => s.selectionType === type).map(s => s.selectionValue);
  // Software + Citrix count for display purposes (Citrix lives in Software section but uses its own selectionType)
  const softwareAndCitrixCount = countByType('Software') + (isSelected('Citrix', 'Citrix Access') ? 1 : 0);

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Request' : 'New Request'}
        subtitle={isEdit && existingRequest
          ? `Editing request for ${existingRequest.fullName}`
          : 'Create an onboarding, offboarding, or department transfer request'
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Returned banner */}
      {existingRequest?.status === 'Returned' && (
        <div className={styles.returnedBanner}>
          <strong>Returned for changes</strong>
          <p>This request was returned by the approver. Please review the comments below and make the necessary corrections.</p>
          {existingRequest.comments
            .filter(c => c.commentText.startsWith('[Returned'))
            .slice(-1)
            .map(c => (
              <div key={c.commentId} className={styles.returnedComment}>
                <em>{c.authorDisplayName}:</em> {c.commentText.replace('[Returned for changes] ', '')}
              </div>
            ))
          }
        </div>
      )}

      {/* ── Wizard Progress Bar ── */}
      <div className={styles.wizardProgress}>
        <div className={styles.wizardTrack}>
          <div className={styles.wizardTrackFill} style={{ width: `${progressPercent}%` }} />
        </div>
        <div className={styles.wizardSteps}>
          {WIZARD_STEPS.map((step, idx) => (
            <button
              key={step.key}
              className={`${styles.wizardStep} ${idx === currentStep ? styles.wizardStepActive : ''} ${idx < currentStep ? styles.wizardStepComplete : ''}`}
              onClick={() => goToStep(idx)}
              type="button"
            >
              <span className={styles.wizardStepNumber}>
                {idx < currentStep ? '✓' : idx + 1}
              </span>
              <span className={styles.wizardStepLabel}>{step.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Step validation errors ── */}
      {stepErrors.length > 0 && (
        <div className={styles.stepErrorBanner}>
          <strong>Please fix the following before continuing:</strong>
          <ul>
            {stepErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* STEP 0: Request Type & Employee Details                     */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {currentStep === 0 && (
        <>
          <Card className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Request Type</h3>
            <div className={styles.typeToggle}>
              <button className={`${styles.typeBtn} ${requestType === 'Onboarding' ? styles.typeBtnActive : ''}`} onClick={() => handleRequestTypeChange('Onboarding')} type="button">
                🟢 Onboarding
              </button>
              <button className={`${styles.typeBtn} ${requestType === 'Offboarding' ? styles.typeBtnActive : ''}`} onClick={() => handleRequestTypeChange('Offboarding')} type="button">
                🔴 Offboarding
              </button>
              <button className={`${styles.typeBtn} ${requestType === 'Transfer' ? styles.typeBtnActive : ''}`} onClick={() => handleRequestTypeChange('Transfer')} type="button">
                🔄 Department Transfer
              </button>
            </div>
          </Card>

          {/* Existing-employee picker — Offboarding & Transfer act on a real employee record */}
          {isExistingEmployeeType && (
            <Card className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Select Employee</h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>Employee *</label>
                <select className={styles.select} value={selectedEmployeeId} onChange={e => handleEmployeeSelect(e.target.value)}>
                  <option value="">Search and select an employee...</option>
                  {employees.map(emp => (
                    <option key={emp.employeeId} value={emp.employeeId}>
                      {emp.fullName}{emp.vistaId ? ` (${emp.vistaId})` : ''} — {emp.department}
                    </option>
                  ))}
                </select>
                <span className={styles.fieldHint}>
                  {requestType === 'Offboarding'
                    ? 'Choose the employee who is leaving. Their details are filled in automatically and cannot be edited.'
                    : 'Choose the employee who is transferring. Their current details are filled in automatically and cannot be edited.'}
                </span>
              </div>
            </Card>
          )}

          <Card className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Employee Details</h3>
            {isExistingEmployeeType && !selectedEmployeeId ? (
              <p className={styles.muted} style={{ fontSize: 13 }}>
                Select an employee above to populate these details.
              </p>
            ) : (
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>OQT ID</label>
                <input className={styles.input} value={oqtEmployeeId} onChange={e => handleOqtIdChange(e.target.value)} placeholder="e.g., JDO" maxLength={10} disabled={isExistingEmployeeType} readOnly={isExistingEmployeeType} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Full Name *</label>
                <input className={styles.input} value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Doe" disabled={isExistingEmployeeType} readOnly={isExistingEmployeeType} />
              </div>
              {!isExistingEmployeeType && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Preferred Initials</label>
                  <input className={styles.input} value={preferredInitials} onChange={e => setPreferredInitials(e.target.value)} placeholder="JD" maxLength={10} />
                </div>
              )}
              <div className={styles.formGroup}>
                <label className={styles.label}>Email Address{isExistingEmployeeType ? ' *' : ''}</label>
                <input className={styles.input} type="email" value={oqtEmailAddress} onChange={e => { setOqtEmailAddress(e.target.value); setEmailEdited(true); }} placeholder="john.doe@omantrading.com" disabled={isExistingEmployeeType} readOnly={isExistingEmployeeType} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Designation *</label>
                <input className={styles.input} value={designation} onChange={e => setDesignation(e.target.value)} placeholder="Software Engineer" disabled={isExistingEmployeeType} readOnly={isExistingEmployeeType} />
              </div>
              {!isExistingEmployeeType && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Employment Type *</label>
                  <select className={styles.select} value={employmentType} onChange={e => setEmploymentType(e.target.value)}>
                    {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
              <div className={styles.formGroup}>
                <label className={styles.label}>Department *</label>
                {isExistingEmployeeType ? (
                  <input className={styles.input} value={department} disabled readOnly />
                ) : (
                  <select className={styles.select} value={department} onChange={e => handleDepartmentChange(e.target.value)}>
                    <option value="">Select department...</option>
                    {departments.map(d => <option key={d.departmentId} value={d.name}>{d.name}</option>)}
                  </select>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Office Location *</label>
                {isExistingEmployeeType ? (
                  <input className={styles.input} value={officeLocation} disabled readOnly />
                ) : (
                  <select className={styles.select} value={officeLocation} onChange={e => setOfficeLocation(e.target.value)}>
                    <option value="">Select location...</option>
                    {locations.map(l => <option key={l.officeLocationId} value={l.name}>{l.name}</option>)}
                  </select>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{isExistingEmployeeType ? 'Current Line Manager' : 'Line Manager *'}</label>
                {isExistingEmployeeType ? (
                  <input className={styles.input} value={lineManagerEmail || '— none on record —'} disabled readOnly />
                ) : (
                  <>
                    <select className={styles.select} value={lineManagerEmail} onChange={e => setLineManagerEmail(e.target.value)}>
                      <option value="">Select line manager...</option>
                      {managers.map(m => <option key={m.id} value={m.email}>{m.fullName} ({m.email})</option>)}
                    </select>
                    <span className={styles.fieldHint}>{requestType === 'Onboarding' ? 'This manager will be notified and asked to confirm technology requirements' : 'Approval will be routed to the selected manager'}</span>
                  </>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{effectiveDateLabel} *</label>
                <input className={styles.input} type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} />
                <span className={styles.fieldHint}>{requestType === 'Onboarding' ? 'Start date' : requestType === 'Transfer' ? 'Transfer date' : 'Last working day'}</span>
              </div>
            </div>
            )}
          </Card>

          {/* Transfer: Optional New Line Manager */}
          {requestType === 'Transfer' && (
            <Card className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Transfer Details</h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>New Line Manager (optional)</label>
                  <select className={styles.select} value={newLineManagerEmail} onChange={e => setNewLineManagerEmail(e.target.value)}>
                    <option value="">Same as current manager</option>
                    {managers.map(m => <option key={m.id} value={m.email}>{m.fullName} ({m.email})</option>)}
                  </select>
                  <span className={styles.fieldHint}>If the employee is moving to a new team, select their new manager. Approval will be routed to the new manager if selected.</span>
                </div>
              </div>
            </Card>
          )}

          {/* Offboarding: Instructions */}
          {requestType === 'Offboarding' && (
            <Card className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Offboarding Instructions</h3>
              <p className={styles.fieldHint} style={{ marginBottom: 12 }}>All fields are optional. Provide any relevant details for IT to action during offboarding.</p>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Forward Emails To</label>
                  <input className={styles.input} type="email" value={forwardEmailTo} onChange={e => setForwardEmailTo(e.target.value)} placeholder="colleague@omantrading.com" />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>System Access End Date</label>
                  <input className={styles.input} type="date" value={deactivationDate} max={effectiveDate || undefined} onChange={e => setDeactivationDate(e.target.value)} />
                  <span className={styles.fieldHint}>Access is removed at end of day. Leave blank to use the last working day. Cannot be after the last working day. For immediate removal, contact IT directly.</span>
                </div>
              </div>
              <div className={styles.formGroup} style={{ marginTop: 8 }}>
                <label className={styles.label}>Out-of-Office Message</label>
                <textarea className={styles.textarea} value={outOfOfficeMessage} onChange={e => setOutOfOfficeMessage(e.target.value)} placeholder="Auto-reply message to set on the employee's mailbox..." rows={2} />
              </div>
              <div className={styles.formGroup} style={{ marginTop: 8 }}>
                <label className={styles.label}>Data / Files Handover Instructions</label>
                <textarea className={styles.textarea} value={handoverInstructions} onChange={e => setHandoverInstructions(e.target.value)} placeholder="Who should receive access to this person's files, shared drives, etc..." rows={2} />
              </div>
              <div className={styles.formGroup} style={{ marginTop: 8 }}>
                <label className={styles.label}>Last Working Day Reminder</label>
                <textarea className={styles.textarea} value={lastDayReminder} onChange={e => setLastDayReminder(e.target.value)} placeholder="Any special instructions for the last day..." rows={2} />
              </div>
            </Card>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* STEP 1: Hardware                                            */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {currentStep === 1 && (
        <Card className={styles.formSection}>
          <h3 className={styles.sectionTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Hardware</span>
            {requestType === 'Transfer' && (
              <button className={styles.btnGhost} onClick={() => setHwExpanded(!hwExpanded)} type="button" style={{ fontSize: 13 }}>
                {hwExpanded ? '▾ Collapse' : '▸ Expand'} ({countByType('Hardware')} selected)
              </button>
            )}
          </h3>
          {requestType === 'Transfer' && !hwExpanded ? (
            <p className={styles.muted} style={{ fontSize: 13 }}>
              {countByType('Hardware')} items selected. Click Expand to modify.
            </p>
          ) : (
          <>
          <div className={styles.selectionsGrid}>
            <div className={styles.selectionCategory}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={isSelected('Hardware', 'Desktop')} onChange={() => toggleSelection('Hardware', 'Desktop')} className={styles.checkbox} />
                <span><strong>{hw('Provide Desktop', 'Collect Desktop')}</strong></span>
              </label>
              {isSelected('Hardware', 'Desktop') && (
                <div style={{ marginLeft: 24 }}>
                  <label className={styles.checkboxLabel}>
                    <input type="checkbox" checked={isSelected('Hardware', 'Camera')} onChange={() => toggleSelection('Hardware', 'Camera')} className={styles.checkbox} />
                    <span>{hw('Provide Camera', 'Collect Camera')}</span>
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input type="checkbox" checked={isSelected('Hardware', 'Headset')} onChange={() => toggleSelection('Hardware', 'Headset')} className={styles.checkbox} />
                    <span>{hw('Provide Headset', 'Collect Headset')}</span>
                  </label>
                </div>
              )}
            </div>

            <div className={styles.selectionCategory}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={isSelected('Hardware', 'Laptop')} onChange={() => toggleSelection('Hardware', 'Laptop')} className={styles.checkbox} />
                <span><strong>{hw('Provide Laptop', 'Collect Laptop')}</strong></span>
              </label>
            </div>

            <div className={styles.selectionCategory}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={isSelected('Hardware', 'iPhone')} onChange={() => toggleSelection('Hardware', 'iPhone')} className={styles.checkbox} />
                <span><strong>{hw('Provide iPhone', 'Collect iPhone')}</strong></span>
              </label>
              {isSelected('Hardware', 'iPhone') && (
                <div style={{ marginLeft: 24, marginTop: 4 }}>
                  <label className={styles.label}>iPhone Type</label>
                  <input className={styles.input} value={iPhoneType} onChange={e => setIPhoneType(e.target.value)} placeholder="Any available" style={{ maxWidth: 250 }} />
                </div>
              )}
            </div>

            <div className={styles.selectionCategory}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={isSelected('Hardware', 'iPad')} onChange={() => toggleSelection('Hardware', 'iPad')} className={styles.checkbox} />
                <span>{hw('Provide iPad', 'Collect iPad')}</span>
              </label>
            </div>
          </div>

          <div className={styles.formGrid} style={{ marginTop: 16 }}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Desk Phone Extension #</label>
              <input className={styles.input} type="number" value={deskPhoneExt} onChange={e => setDeskPhoneExt(e.target.value)} placeholder="Extension number" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Mobile Number</label>
              <input className={styles.input} value={mobileNumber} onChange={e => setMobileNumber(e.target.value)} placeholder="Mobile number" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Mobile Package</label>
              <select className={styles.select} value={mobilePackage} onChange={e => setMobilePackage(e.target.value)}>
                {MOBILE_PACKAGES.map(pkg => <option key={pkg} value={pkg}>{pkg}</option>)}
              </select>
            </div>
          </div>

          <div className={styles.formGroup} style={{ marginTop: 12 }}>
            <label className={styles.label}>Hardware Remarks</label>
            <textarea className={styles.textarea} value={hardwareRemarks} onChange={e => setHardwareRemarks(e.target.value)} placeholder="Any additional hardware requirements or notes..." rows={2} />
          </div>
          </>
          )}
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* STEP 2: Software & Applications                             */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {currentStep === 2 && (
        <Card className={styles.formSection}>
          <h3 className={styles.sectionTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Software &amp; Applications</span>
            {requestType === 'Transfer' && (
              <button className={styles.btnGhost} onClick={() => setSwExpanded(!swExpanded)} type="button" style={{ fontSize: 13 }}>
                {swExpanded ? '▾ Collapse' : '▸ Expand'} ({softwareAndCitrixCount} selected)
              </button>
            )}
          </h3>
          {requestType === 'Transfer' && !swExpanded ? (
            <p className={styles.muted} style={{ fontSize: 13 }}>
              {softwareAndCitrixCount} items selected. Click Expand to modify.
            </p>
          ) : (
          <>
          <div className={styles.selectionOptions}>
            {SOFTWARE_ITEMS.map(app => (
              <label key={app} className={styles.checkboxLabel}>
                <input type="checkbox" checked={isSelected('Software', app)} onChange={() => toggleSelection('Software', app)} className={styles.checkbox} />
                <span>{isOffboarding ? `Revoke ${app}` : `Install ${app}`}</span>
              </label>
            ))}
          </div>

          {/* Citrix Access — logically software/remote access, uses its own selectionType for task mapping */}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E5E7EB' }}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={isSelected('Citrix', 'Citrix Access')} onChange={() => toggleSelection('Citrix', 'Citrix Access')} className={styles.checkbox} />
              <span><strong>{hw('Enrol Citrix Access', 'Revoke Citrix Access')}</strong></span>
            </label>
          </div>

          <div className={styles.formGroup} style={{ marginTop: 12 }}>
            <label className={styles.label}>Software Remarks</label>
            <textarea className={styles.textarea} value={softwareRemarks} onChange={e => setSoftwareRemarks(e.target.value)} placeholder="Any additional software requirements or notes..." rows={2} />
          </div>
          </>
          )}
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* STEP 3: Folder Access + Distribution                         */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {currentStep === 3 && (
        <>
          <Card className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Folder Access</h3>

            {/* Transfer: three-way radio per folder */}
            {requestType === 'Transfer' ? (
              <div>
                <p className={styles.fieldHint} style={{ marginBottom: 12 }}>For each folder, indicate whether access should be added, revoked, or left unchanged.</p>
                <table className={styles.table} style={{ fontSize: 14 }}>
                  <thead>
                    <tr>
                      <th>Folder</th>
                      <th style={{ textAlign: 'center', width: 100 }}>No Change</th>
                      <th style={{ textAlign: 'center', width: 100 }}>Add</th>
                      <th style={{ textAlign: 'center', width: 100 }}>Revoke</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FOLDER_ACCESS_ITEMS.map(folder => (
                      <tr key={folder}>
                        <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{folder}</td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="radio" name={`folder-${folder}`} checked={folderTransferActions[folder] === 'NoChange'} onChange={() => setFolderAction(folder, 'NoChange')} />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="radio" name={`folder-${folder}`} checked={folderTransferActions[folder] === 'Add'} onChange={() => setFolderAction(folder, 'Add')} />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="radio" name={`folder-${folder}`} checked={folderTransferActions[folder] === 'Revoke'} onChange={() => setFolderAction(folder, 'Revoke')} />
                        </td>
                        <td>
                          {folderTransferActions[folder] !== 'NoChange' && (
                            <input
                              className={styles.input}
                              value={folderRemarks[folder] || ''}
                              onChange={e => updateFolderRemark(folder, e.target.value)}
                              placeholder="Folder path or notes..."
                              style={{ width: '100%', padding: '6px 10px', fontSize: 13 }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Onboarding / Offboarding: standard checkboxes */
              <div className={styles.selectionOptions}>
                {FOLDER_ACCESS_ITEMS.map(folder => (
                  <div key={folder} style={{ marginBottom: 8 }}>
                    <label className={styles.checkboxLabel}>
                      <input type="checkbox" checked={isSelected('FolderAccess', folder)} onChange={() => toggleSelection('FolderAccess', folder)} className={styles.checkbox} />
                      <span>{isOffboarding ? `Revoke ${folder}` : `Grant ${folder}`}</span>
                    </label>
                    {isSelected('FolderAccess', folder) && (
                      <input
                        className={styles.input}
                        value={folderRemarks[folder] || ''}
                        onChange={e => updateFolderRemark(folder, e.target.value)}
                        placeholder="Specify folder path or permissions..."
                        style={{ marginLeft: 28, marginTop: 4, maxWidth: 400 }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <div className={styles.formGroup}>
                <label className={styles.label}>App / Batch Report Distribution</label>
                <textarea className={styles.textarea} value={appBatchDistribution} onChange={e => setAppBatchDistribution(e.target.value)} placeholder="Specify report/batch distribution details..." rows={2} />
              </div>
              <div className={styles.formGroup} style={{ marginTop: 8 }}>
                <label className={styles.label}>Email / Distribution Lists</label>
                <textarea className={styles.textarea} value={emailDistributionLists} onChange={e => setEmailDistributionLists(e.target.value)} placeholder="Specify email groups..." rows={2} />
              </div>
              <div className={styles.formGroup} style={{ marginTop: 8 }}>
                <label className={styles.label}>Other</label>
                <textarea className={styles.textarea} value={otherAccess} onChange={e => setOtherAccess(e.target.value)} placeholder="Any other access requirements..." rows={2} />
              </div>
            </div>
          </Card>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* STEP 4: Review & Submit                                     */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {currentStep === 4 && (
        <div className={styles.reviewStep}>
          <Card className={styles.formSection}>
            <div className={styles.reviewHeader}>
              <h3 className={styles.sectionTitle}>Review Your Request</h3>
              <p className={styles.reviewSubtitle}>Please review all details before submitting. Click any section header to go back and edit.</p>
            </div>

            {/* Employee Summary */}
            <div className={styles.reviewSection}>
              <button className={styles.reviewSectionTitle} onClick={() => goToStep(0)} type="button">
                <span>Request Type &amp; Employee Details</span>
                <span className={styles.reviewEditHint}>Edit →</span>
              </button>
              <div className={styles.reviewGrid}>
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>Request Type</span>
                  <span className={styles.reviewValue}>{requestType}</span>
                </div>
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>OQT ID</span>
                  <span className={styles.reviewValue}>{oqtEmployeeId || '—'}</span>
                </div>
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>Full Name</span>
                  <span className={styles.reviewValue}>{fullName}</span>
                </div>
                {preferredInitials && (
                  <div className={styles.reviewField}>
                    <span className={styles.reviewLabel}>Initials</span>
                    <span className={styles.reviewValue}>{preferredInitials}</span>
                  </div>
                )}
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>Email</span>
                  <span className={styles.reviewValue}>{oqtEmailAddress}</span>
                </div>
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>Designation</span>
                  <span className={styles.reviewValue}>{designation}</span>
                </div>
                {requestType === 'Onboarding' && (
                  <div className={styles.reviewField}>
                    <span className={styles.reviewLabel}>Employment Type</span>
                    <span className={styles.reviewValue}>{employmentType}</span>
                  </div>
                )}
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>Department</span>
                  <span className={styles.reviewValue}>{department}</span>
                </div>
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>Office</span>
                  <span className={styles.reviewValue}>{officeLocation}</span>
                </div>
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>Line Manager</span>
                  <span className={styles.reviewValue}>{lineManagerEmail}</span>
                </div>
                <div className={styles.reviewField}>
                  <span className={styles.reviewLabel}>{effectiveDateLabel}</span>
                  <span className={styles.reviewValue}>{effectiveDate}</span>
                </div>
              </div>
            </div>

            {/* Hardware Summary */}
            <div className={styles.reviewSection}>
              <button className={styles.reviewSectionTitle} onClick={() => goToStep(1)} type="button">
                <span>Hardware ({countByType('Hardware')} items)</span>
                <span className={styles.reviewEditHint}>Edit →</span>
              </button>
              <div className={styles.reviewTagList}>
                {getSelectedItems('Hardware').map(item => (
                  <span key={item} className={styles.reviewTag}>{item}</span>
                ))}
                {countByType('Hardware') === 0 && (
                  <span className={styles.reviewNone}>No hardware selected</span>
                )}
              </div>
              {iPhoneType && <p className={styles.reviewNote}>iPhone Type: {iPhoneType}</p>}
              {deskPhoneExt && <p className={styles.reviewNote}>Desk Phone Ext: {deskPhoneExt}</p>}
              {mobileNumber && <p className={styles.reviewNote}>Mobile: {mobileNumber}</p>}
              {mobilePackage && <p className={styles.reviewNote}>Package: {mobilePackage}</p>}
              {hardwareRemarks && <p className={styles.reviewNote}>Remarks: {hardwareRemarks}</p>}
            </div>

            {/* Software Summary */}
            <div className={styles.reviewSection}>
              <button className={styles.reviewSectionTitle} onClick={() => goToStep(2)} type="button">
                <span>Software ({softwareAndCitrixCount} items)</span>
                <span className={styles.reviewEditHint}>Edit →</span>
              </button>
              <div className={styles.reviewTagList}>
                {getSelectedItems('Software').map(item => (
                  <span key={item} className={styles.reviewTag}>{item}</span>
                ))}
                {isSelected('Citrix', 'Citrix Access') && (
                  <span className={styles.reviewTag}>Citrix Access</span>
                )}
                {softwareAndCitrixCount === 0 && (
                  <span className={styles.reviewNone}>No software selected</span>
                )}
              </div>
              {softwareRemarks && <p className={styles.reviewNote}>Remarks: {softwareRemarks}</p>}
            </div>

            {/* Access Summary */}
            <div className={styles.reviewSection}>
              <button className={styles.reviewSectionTitle} onClick={() => goToStep(3)} type="button">
                <span>Access &amp; Folders ({countByType('FolderAccess')} folders)</span>
                <span className={styles.reviewEditHint}>Edit →</span>
              </button>
              {requestType === 'Transfer' ? (
                <div className={styles.reviewTagList}>
                  {FOLDER_ACCESS_ITEMS.filter(f => folderTransferActions[f] !== 'NoChange').map(folder => (
                    <span key={folder} className={`${styles.reviewTag} ${folderTransferActions[folder] === 'Revoke' ? styles.reviewTagRevoke : ''}`}>
                      {folderTransferActions[folder] === 'Add' ? '+ ' : '− '}{folder}
                      {folderRemarks[folder] && <span className={styles.reviewTagMeta}> — {folderRemarks[folder]}</span>}
                    </span>
                  ))}
                  {FOLDER_ACCESS_ITEMS.every(f => folderTransferActions[f] === 'NoChange') && (
                    <span className={styles.reviewNone}>No folder changes</span>
                  )}
                </div>
              ) : (
                <div className={styles.reviewTagList}>
                  {getSelectedItems('FolderAccess').map(item => (
                    <span key={item} className={styles.reviewTag}>
                      {item}
                      {folderRemarks[item] && <span className={styles.reviewTagMeta}> — {folderRemarks[item]}</span>}
                    </span>
                  ))}
                  {countByType('FolderAccess') === 0 && (
                    <span className={styles.reviewNone}>No folders selected</span>
                  )}
                </div>
              )}
              {appBatchDistribution && <p className={styles.reviewNote}>App/Batch Distribution: {appBatchDistribution}</p>}
              {emailDistributionLists && <p className={styles.reviewNote}>Email/Distribution Lists: {emailDistributionLists}</p>}
              {otherAccess && <p className={styles.reviewNote}>Other: {otherAccess}</p>}
            </div>

            {/* Technology (always included) */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewSectionTitleStatic}>
                <span>Technology (auto-included)</span>
              </div>
              <div className={styles.reviewTagList}>
                {['Fingerprint Access', 'Printer Access', 'AV', 'Office 365 License', 'Scanner Address Book', 'Added to Default DLs'].map(item => (
                  <span key={item} className={`${styles.reviewTag} ${styles.reviewTagMuted}`}>{item}</span>
                ))}
              </div>
            </div>

            {/* Offboarding Instructions (review) */}
            {requestType === 'Offboarding' && (forwardEmailTo || outOfOfficeMessage || lastDayReminder || handoverInstructions || deactivationDate) && (
              <div className={styles.reviewSection}>
                <div className={styles.reviewSectionTitleStatic}>
                  <span>Offboarding Instructions</span>
                </div>
                <div className={styles.reviewGrid}>
                  {forwardEmailTo && <div className={styles.reviewField}><span className={styles.reviewLabel}>Forward Emails To</span><span className={styles.reviewValue}>{forwardEmailTo}</span></div>}
                  {deactivationDate && <div className={styles.reviewField}><span className={styles.reviewLabel}>System Access End Date</span><span className={styles.reviewValue}>{deactivationDate}</span></div>}
                  {outOfOfficeMessage && <div className={styles.reviewField}><span className={styles.reviewLabel}>Out-of-Office</span><span className={styles.reviewValue}>{outOfOfficeMessage}</span></div>}
                  {handoverInstructions && <div className={styles.reviewField}><span className={styles.reviewLabel}>Handover</span><span className={styles.reviewValue}>{handoverInstructions}</span></div>}
                  {lastDayReminder && <div className={styles.reviewField}><span className={styles.reviewLabel}>Last Day Reminder</span><span className={styles.reviewValue}>{lastDayReminder}</span></div>}
                </div>
              </div>
            )}

            {/* Transfer: New Manager (review) */}
            {requestType === 'Transfer' && newLineManagerEmail && (
              <div className={styles.reviewSection}>
                <div className={styles.reviewSectionTitleStatic}>
                  <span>Transfer Details</span>
                </div>
                <div className={styles.reviewGrid}>
                  <div className={styles.reviewField}>
                    <span className={styles.reviewLabel}>New Line Manager</span>
                    <span className={styles.reviewValue}>{newLineManagerEmail}</span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── Wizard Navigation ── */}
      <div className={styles.wizardNav}>
        {currentStep > 0 && (
          <button className={styles.btnSecondary} onClick={goBack} disabled={saving} type="button">
            ← Back
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button className={styles.btnGhost} onClick={handleCancel} disabled={saving} type="button">
          Cancel
        </button>
        <button className={styles.btnOutline} onClick={handleSave} disabled={saving} type="button">
          {saving ? 'Saving...' : 'Save as Draft'}
        </button>
        {currentStep < WIZARD_STEPS.length - 1 ? (
          <button className={styles.btnPrimary} onClick={goNext} disabled={saving} type="button">
            Next →
          </button>
        ) : (
          <button className={styles.btnPrimary} onClick={handleSaveAndSubmit} disabled={saving} type="button">
            {saving ? 'Submitting...' : (existingRequest?.status === 'Returned' ? 'Save & Resubmit' : 'Save & Submit')}
          </button>
        )}
      </div>
    </div>
  );
}
